import React from 'react'
import { Info } from 'lucide-react'
import { Accordion } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import type { MenubarMenuModel } from '../types/menubar-menu-model'
import { MenubarTriggerButton } from './menubar-trigger-button'
import { MobileEditSection } from './mobile-edit-section'
import { MobileFileSection } from './mobile-file-section'
import { MobileHelpSection } from './mobile-help-section'
import { MobileObjectSection } from './mobile-object-section'
import { MobileViewSection } from './mobile-view-section'

type MobileMenuProps = {
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  model: MenubarMenuModel
}

/** Mobile: Sheet with Accordion using styled MenubarItem components */
export const MobileMenu: React.FC<MobileMenuProps> = ({ open, onOpenChange, model }) => {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <MenubarTriggerButton />
      </SheetTrigger>
      <SheetContent side="left" className="w-[248px] p-4">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-sm font-medium leading-[20px]">Menu</SheetTitle>
          <SheetDescription className="sr-only">
            Application menu with navigation options
          </SheetDescription>
        </SheetHeader>

        {/* Separator */}
        <div className="h-px bg-border mb-4" />

        {/* Info Badge */}
        <Badge variant="outline" className="mb-4 w-full justify-start gap-4 py-0.5 px-2">
          <Info className="h-3 w-3 shrink-0" />
          <span className="text-xs font-semibold leading-[16px]">
            Full editing on desktop. Switch to desktop or tablet for a better experience.
          </span>
        </Badge>

        {/* Accordion with styled Menubar items */}
        <Accordion type="single" collapsible className="w-full">
          <MobileFileSection
            onClose={model.onClose}
            openPreferencesDialog={model.openPreferencesDialog}
          />
          <MobileEditSection commands={model.editCommands} closeMenu={model.closeMenu} />
          <MobileObjectSection commands={model.objectCommands} closeMenu={model.closeMenu} />
          <MobileViewSection
            gridMode={model.gridMode}
            onToggleFullPica={model.onToggleFullPica}
            onToggleHalfPica={model.onToggleHalfPica}
            viewToggles={model.viewToggles}
            openGuidesDialog={model.openGuidesDialog}
          />
          <MobileHelpSection />
        </Accordion>
      </SheetContent>
    </Sheet>
  )
}
